import React from 'react';
import Icon from '../../../components/AppIcon';

const TeamCredits = ({ teamMembers, projectLead }) => {
  return (
    <section className="py-16 bg-card">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-black text-primary mb-4 color-brand-dark">
              Academic & Mentor Team
            </h2>
            <p className="text-xl text-muted-foreground">
              Meet the educators and mentors who turn complex semiconductor science into clear, career-ready skills
            </p>
          </div>

          {/* Project Lead */}
          <div className="mb-12 lg:mb-16">
            <div className="bg-[#f4eedd] p-6 lg:p-8 xl:p-12 border-2 border-black brutalist-shadow" style={{ color: '#002468' }}>
              <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-4 lg:space-y-0 lg:space-x-6 xl:space-x-8">
                <div className="w-20 h-20 lg:w-28 lg:h-28 xl:w-32 xl:h-32 border-2 lg:border-4 border-accent-foreground flex items-center justify-center">
                  <Icon name="User" size={32} className="lg:w-10 lg:h-10 xl:w-12 xl:h-12" color="white" />
                </div>

                <div className="flex-1 text-center lg:text-left">
                  <h3 className="text-xl lg:text-2xl font-black mb-1 lg:mb-2">{projectLead?.name}</h3>
                  <div className="text-base lg:text-lg font-semibold mb-3 lg:mb-4 opacity-90">{projectLead?.role}</div>
                  <p className="text-accent-foreground/80 leading-relaxed text-sm lg:text-base mb-3 lg:mb-4">
                    {projectLead?.bio}
                  </p>
                  <div className="flex flex-wrap justify-center lg:justify-start gap-1.5 lg:gap-2">
                    {projectLead?.skills?.map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 lg:px-3 py-1 bg-transparent text-accent text-xs lg:text-sm font-semibold border border-black"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Team Members Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers?.map((member, index) => (
              <div key={index} className="bg-background border-2 border-concrete p-6 brutalist-shadow hover:border-white hover:shadow-none transition-shadow duration-300">
                <div className="text-center">
                  <div className="w-24 h-24 border-2 border-concrete flex items-center justify-center">
                    <Icon name="User" size={32} className="text-concrete-dark" />
                  </div>
                  
                  <h4 className="font-bold text-primary mb-1 color-brand-dark">{member?.name}</h4>
                  <div className="text-sm font-semibold text-accent mb-3">{member?.role}</div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                    {member?.contribution}
                  </p>
                  
                  {/* Skills */}
                  <div className="flex flex-wrap justify-center gap-1">
                    {member?.skills?.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-[#f4eedd] text-xs font-medium text-primary border border-concrete"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>

                  {/* Contact */}
                  {member?.contact && (
                    <div className="mt-4 pt-4 border-t border-black">
                      <div className="flex justify-center space-x-3">
                        {member?.contact?.linkedin && (
                          <a
                            href={member?.contact?.linkedin}
                            className="text-muted-foreground hover:text-accent transition-colors duration-300"
                            aria-label={`${member?.name} LinkedIn profile`}
                          >
                            <Icon name="Linkedin" size={16} />
                          </a>
                        )}
                        {member?.contact?.portfolio && (
                          <a
                            href={member?.contact?.portfolio}
                            className="text-muted-foreground hover:text-accent transition-colors duration-300"
                            aria-label={`${member?.name} portfolio`}
                          >
                            <Icon name="ExternalLink" size={16} />
                          </a>
                        )}
                        {member?.contact?.email && (
                          <a
                            href={`mailto:${member?.contact?.email}`}
                            className="text-muted-foreground hover:text-accent transition-colors duration-300"
                            aria-label={`Email ${member?.name}`}
                          >
                            <Icon name="Mail" size={16} />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Collaboration Note */}
          <div className="mt-16 text-center">
            <div className="inline-block p-6 bg-[#f4eedd] border-2 border-black">
              <Icon name="Users" size={32} className="mx-auto mb-4 text-accent" />
              <h4 className="font-bold text-primary mb-2 color-brand-dark">Interested in Mentoring or Partnering on Labs?</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Our team is always open to working with industry experts and educational partners.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-accent text-accent-foreground font-semibold border-2 border-black hover:bg-accent/90 transition-colors duration-300"
              >
                <span>Get in Touch</span>
                <Icon name="ArrowRight" size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamCredits;